/**
 * Premios — Public page consolidating all the restaurant's awards & recognitions.
 *
 * Sections (top to bottom):
 *   1. Hero "Capítulo VI · Premios & reconocimientos"
 *   2. Featured 2024 Restaurant Guru poster with Andrew Priest's official quote.
 *   3. Six-year timeline grid (2021–2026). The 2024 cell uses the real poster
 *      asset; the other five cells render a stylized typographic card with the
 *      same visual rhythm (replace with real posters when available).
 *   4. Two-up cards for "Nº 1 TripAdvisor Ocaña" and "Mención ARQUEOPRAXIS".
 *   5. CTA — reservar mesa / visitas guiadas.
 *
 * SEO:
 *   - Title + meta description + custom og-premios.jpg.
 *   - JSON-LD `ItemList` of `Award` items (PREMIOS_JSONLD) — Google can surface
 *     these as a horizontal carousel rich result.
 *   - Breadcrumb Inicio → Premios.
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Star, Award, Trophy, ArrowRight, MapPin } from "lucide-react";
import SEO from "@/components/seo/SEO";
import { SITE_URL, RESTAURANT_NAME } from "@/lib/jsonld";

// 6 años de Restaurant Guru — solo 2024 tiene cartel oficial descargado;
// el resto se representa con tarjeta tipográfica del mismo lenguaje gráfico.
const GURU_YEARS = [
    { year: 2021, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
    { year: 2022, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
    { year: 2023, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
    { year: 2024, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
    { year: 2025, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
    { year: 2026, label: "Best Restaurant in Ocaña", poster: "/restaurant-guru-2024.png" },
];

const PREMIOS_JSONLD = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Premios y reconocimientos · Restaurante Museo Tenería",
    description:
        "Restaurant Guru Best Restaurant in Ocaña 2021–2026 (6 años consecutivos), Nº 1 TripAdvisor Ocaña con 4,7★, mención ARQUEOPRAXIS por rehabilitación ejemplar del edificio del siglo XV.",
    url: `${SITE_URL}/premios`,
    itemListElement: [
        ...GURU_YEARS.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "Award",
                name: `Restaurant Guru ${g.year} — ${g.label}`,
                description: `Premio Restaurant Guru ${g.year} a ${RESTAURANT_NAME} como mejor restaurante en Ocaña.`,
            },
        })),
        {
            "@type": "ListItem",
            position: 7,
            item: {
                "@type": "Award",
                name: "Nº 1 TripAdvisor Ocaña",
                description: `${RESTAURANT_NAME} ocupa el primer puesto de TripAdvisor en Ocaña con 4,7★ y más de 1.053 reseñas.`,
            },
        },
        {
            "@type": "ListItem",
            position: 8,
            item: {
                "@type": "Award",
                name: "Mención ARQUEOPRAXIS — rehabilitación ejemplar",
                description: "Mención por la conservación y puesta en valor del edificio del siglo XV (antigua tenería medieval) en la judería de Ocaña.",
            },
        },
    ],
};

function YearCard({ data, index }) {
    const { t } = useTranslation();
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.9, delay: Math.min(index * 0.08, 0.4) }}
            className="relative group"
        >
            {data.poster ? (
                <div className="aspect-[3/4] border border-gold/30 overflow-hidden bg-ivory">
                    <img
                        src={data.poster}
                        alt={`Restaurant Guru ${data.year} — Best Restaurant in Ocaña`}
                        className="w-full h-full object-contain p-2"
                    />
                </div>
            ) : (
                <div className="aspect-[3/4] border border-gold/25 bg-bg-alt flex flex-col items-center justify-center p-6 text-center transition-colors duration-500 group-hover:border-gold/60">
                    <p className="font-editorial italic text-[0.65rem] tracking-[0.3em] uppercase text-gold mb-3">
                        Restaurant Guru
                    </p>
                    <p className="font-display italic text-5xl md:text-6xl text-ivory leading-none">
                        {data.year}
                    </p>
                    <div className="my-5 w-10 h-px bg-gold/50" />
                    <p className="font-editorial italic text-[0.7rem] tracking-[0.2em] uppercase text-gold/80">
                        {t("premios.timeline.bestRestaurant")}
                    </p>
                    <p className="font-body text-xs text-stone mt-1">
                        {t("premios.timeline.inOcana")}
                    </p>
                    <Star
                        size={20}
                        className="text-gold mt-5"
                        fill="currentColor"
                        strokeWidth={0}
                    />
                </div>
            )}
            <p className="mt-3 text-center font-editorial italic text-xs tracking-[0.25em] uppercase text-stone">
                — {data.year} —
            </p>
        </motion.div>
    );
}

export default function Premios() {
    const { t } = useTranslation();
    return (
        <>
            <SEO
                title={t("premios.seo.title")}
                description={t("premios.seo.description")}
                keywords="premios restaurante Ocaña, Restaurant Guru Ocaña, mejor restaurante Ocaña Toledo, TripAdvisor Ocaña, ARQUEOPRAXIS, restaurante premiado Toledo"
                path="/premios"
                image={`${SITE_URL}/og-premios.jpg`}
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.premios"), item: "/premios" },
                ]}
                jsonLd={[PREMIOS_JSONLD]}
            />

            {/* ========== HERO ========== */}
            <section className="relative pt-44 pb-28 md:pt-56 md:pb-40 overflow-hidden">
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none flex items-center justify-center"
                >
                    <span
                        className="font-display italic text-[24vw] leading-none select-none"
                        style={{ color: "rgba(212, 168, 81, 0.035)" }}
                    >
                        VI
                    </span>
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9 }}
                        className="roman-numeral mb-6"
                    >
                        — {t("premios.chapter")} —
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.1 }}
                        className="font-display leading-[0.95] tracking-tight text-ivory font-light text-[clamp(2.8rem,8vw,7rem)]"
                    >
                        {t("premios.title1")} <span className="italic text-gold">{t("premios.title2")}</span>
                        <span className="block italic text-gold">{t("premios.title3")}</span>
                    </motion.h1>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="line-gold my-12"
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.35 }}
                        className="font-body text-lg md:text-xl text-stone leading-relaxed max-w-2xl"
                    >
                        {t("premios.subtitle")}
                    </motion.p>
                </div>
            </section>

            {/* ========== CARTEL OFICIAL 2024 — FEATURED ========== */}
            <section className="relative py-24 md:py-32 bg-bg-alt overflow-hidden">
                <div className="absolute inset-0 paper-grain pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.1 }}
                        className="md:col-span-5"
                    >
                        <div className="bg-ivory border border-gold/30 p-3 shadow-2xl">
                            <img
                                src="/restaurant-guru-2024.png"
                                alt="Cartel oficial Restaurant Guru 2024 — Best Restaurant in Ocaña · Restaurante Museo Tenería"
                                className="w-full h-auto object-contain"
                                data-testid="premios-cartel-oficial-2024"
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.2, delay: 0.15 }}
                        className="md:col-span-6 md:col-start-7"
                    >
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold">
                            — {t("premios.poster.kicker")} —
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl text-ivory font-light leading-[0.95] mt-5">
                            <span className="italic text-gold">{t("premios.poster.title1")}</span>
                            <span className="block">{t("premios.poster.title2")}</span>
                        </h2>
                        <div className="line-gold my-10" />
                        <p className="font-body text-lg text-stone leading-relaxed">
                            <em className="text-ivory italic">
                                {t("premios.poster.quote")}
                            </em>
                        </p>
                        <p className="font-editorial italic text-xs tracking-[0.2em] uppercase text-stone mt-3">
                            — {t("premios.poster.quoteAuthor")}
                        </p>
                        <p className="font-body text-base text-stone leading-relaxed mt-8 max-w-xl">
                            {t("premios.poster.explainer")}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ========== TIMELINE 6 AÑOS ========== */}
            <section className="py-28 md:py-40 border-t border-gold/10">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="text-center mb-20"
                    >
                        <p className="roman-numeral mb-4">— {t("premios.timeline.years")} —</p>
                        <h2 className="font-display text-4xl md:text-6xl text-ivory font-light leading-tight">
                            {t("premios.timeline.title1")} <span className="italic text-gold">{t("premios.timeline.title2")}</span>
                        </h2>
                        <p className="font-body text-base text-stone leading-relaxed max-w-2xl mx-auto mt-8">
                            {t("premios.timeline.body")}
                        </p>
                    </motion.div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
                        {GURU_YEARS.map((g, i) => (
                            <YearCard key={g.year} data={g} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== TRIPADVISOR + ARQUEOPRAXIS ========== */}
            <section className="py-28 md:py-40 bg-bg-alt relative overflow-hidden">
                <div className="absolute inset-0 paper-grain pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="roman-numeral text-center mb-4"
                    >
                        — {t("premios.extras.kicker")} —
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.1 }}
                        className="font-display text-4xl md:text-5xl text-ivory font-light text-center leading-tight"
                    >
                        {t("premios.extras.title1")} <span className="italic text-gold">{t("premios.extras.title2")}</span>
                    </motion.h2>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-14 mt-20">
                        {/* TripAdvisor */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 1 }}
                            className="border border-gold/20 p-10 md:p-14 bg-bg/40 backdrop-blur-sm"
                            data-testid="premios-tripadvisor-card"
                        >
                            <Trophy size={28} className="text-gold mb-6" strokeWidth={1.2} />
                            <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold mb-3">
                                {t("premios.extras.tripadvisor.kicker")}
                            </p>
                            <h3 className="font-display text-5xl md:text-6xl text-ivory font-light italic leading-none">
                                {t("premios.extras.tripadvisor.rank")}
                            </h3>
                            <div className="flex items-center gap-1 mt-6 text-gold">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={16}
                                        fill="currentColor"
                                        strokeWidth={0}
                                    />
                                ))}
                                <span className="font-display italic text-xl text-ivory ml-3">
                                    4,7
                                </span>
                            </div>
                            <p className="font-body text-base text-stone leading-relaxed mt-8">
                                {t("premios.extras.tripadvisor.body", { count: "1.053" })}
                            </p>
                            <a
                                href="https://www.tripadvisor.es/Restaurant_Review-Oca%C3%B1a-Tener%C3%ADa.html"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 mt-8 text-gold font-editorial italic text-sm link-underline"
                                data-cursor="hover"
                                data-testid="premios-tripadvisor-link"
                            >
                                {t("premios.extras.tripadvisor.cta")} <ArrowRight size={14} />
                            </a>
                        </motion.div>

                        {/* ARQUEOPRAXIS */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 1, delay: 0.15 }}
                            className="border border-gold/20 p-10 md:p-14 bg-bg/40 backdrop-blur-sm"
                            data-testid="premios-arqueopraxis-card"
                        >
                            <Award size={28} className="text-gold mb-6" strokeWidth={1.2} />
                            <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold mb-3">
                                {t("premios.extras.arqueopraxis.kicker")}
                            </p>
                            <h3 className="font-display text-3xl md:text-4xl text-ivory font-light italic leading-tight">
                                {t("premios.extras.arqueopraxis.title1")} <span className="text-gold">{t("premios.extras.arqueopraxis.title2")}</span>
                            </h3>
                            <p className="font-editorial italic text-xs tracking-[0.2em] uppercase text-stone mt-6">
                                — {t("premios.extras.arqueopraxis.building")} —
                            </p>
                            <p className="font-body text-base text-stone leading-relaxed mt-8">
                                {t("premios.extras.arqueopraxis.body")}
                            </p>
                            <Link
                                to="/museo"
                                className="inline-flex items-center gap-2 mt-8 text-gold font-editorial italic text-sm link-underline"
                                data-cursor="hover"
                                data-testid="premios-museo-link"
                            >
                                {t("premios.extras.arqueopraxis.cta")} <ArrowRight size={14} />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ========== CTA ========== */}
            <section className="py-28 md:py-40 border-t border-gold/10">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <p className="roman-numeral mb-6">— {t("premios.cta.kicker")} —</p>
                        <h2 className="font-display text-4xl md:text-6xl text-ivory font-light leading-tight">
                            {t("premios.cta.title1")} <span className="italic text-gold">{t("premios.cta.title2")}</span>{t("premios.cta.title3")}
                        </h2>
                        <p className="font-body text-base md:text-lg text-stone leading-relaxed mt-10 max-w-2xl mx-auto">
                            {t("premios.cta.body")}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-14">
                            <Link
                                to="/reservas"
                                className="btn-solid-alt"
                                data-cursor="hover"
                                data-testid="premios-cta-reservar"
                            >
                                {t("premios.cta.ctaBook")}
                            </Link>
                            <Link
                                to="/eventos"
                                className="btn-ghost"
                                data-cursor="hover"
                                data-testid="premios-cta-eventos"
                            >
                                {t("premios.cta.ctaTours")}
                            </Link>
                        </div>
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-stone mt-14 flex items-center justify-center gap-2">
                            <MapPin size={12} className="text-gold" />
                            {t("premios.cta.address")}
                        </p>
                    </motion.div>
                </div>
            </section>
        </>
    );
}
